import type { ExportOutputSpec } from '@/core/export/types'
import {
  type RenderCanvas,
  type RenderSurfaceFactory,
} from '@/core/render/renderDocument'

export function sliceRenderedCanvas<TCanvas extends RenderCanvas>(
  rendered: TCanvas,
  spec: ExportOutputSpec,
  background: string,
  createSurface: RenderSurfaceFactory<TCanvas>,
): TCanvas {
  const { sourceRect, targetSize } = spec
  if (sourceRect.width <= 0 || sourceRect.height <= 0
    || !Number.isInteger(targetSize.width) || !Number.isInteger(targetSize.height)
    || targetSize.width <= 0 || targetSize.height <= 0) {
    throw new Error('Output slice requires a positive source rectangle and positive integer target size')
  }
  if (sourceRect.x === 0 && sourceRect.y === 0
    && sourceRect.width === rendered.width && sourceRect.height === rendered.height
    && targetSize.width === rendered.width && targetSize.height === rendered.height) {
    return rendered
  }

  const surface = createSurface(targetSize)
  const context = surface.context
  context.fillStyle = background
  context.fillRect(0, 0, targetSize.width, targetSize.height)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'

  const left = Math.max(0, sourceRect.x)
  const top = Math.max(0, sourceRect.y)
  const right = Math.min(rendered.width, sourceRect.x + sourceRect.width)
  const bottom = Math.min(rendered.height, sourceRect.y + sourceRect.height)
  if (right <= left || bottom <= top) return surface.canvas
  const scaleX = targetSize.width / sourceRect.width
  const scaleY = targetSize.height / sourceRect.height
  context.drawImage(
    rendered,
    left,
    top,
    right - left,
    bottom - top,
    (left - sourceRect.x) * scaleX,
    (top - sourceRect.y) * scaleY,
    (right - left) * scaleX,
    (bottom - top) * scaleY,
  )
  return surface.canvas
}
